import axios from 'axios'
import type {
  SymbolsResponse,
  OHLCVCandle,
  SymbolPrediction,
  Alert,
  Interval,
  RegimeResult,
  IndicatorsResult,
  CalendarResponse,
  CurrencyStrengthResult,
  AccuracyStats,
  TradeLogStats,
  SentimentResult,
  StrategistResult,
  TradeRecommendation,
} from '../types'

const apiClient = axios.create({
  baseURL: '',        // Vite proxy / nginx proxy 経由
  timeout: 60_000,
  headers: { 'Content-Type': 'application/json' },
})

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const message =
      error.response?.data?.detail ??
      error.response?.data?.message ??
      error.message ??
      '予期しないエラーが発生しました'
    console.error('[Kronos API Error]', message, error)
    return Promise.reject(new Error(message))
  },
)

// ── Symbols ────────────────────────────────────────────────────────────────

export const fetchSymbols = async (): Promise<SymbolsResponse> => {
  const { data } = await apiClient.get<SymbolsResponse>('/api/symbols')
  return data
}

export const fetchSymbolsMetadata = async (): Promise<Record<string, string>> => {
  const { data } = await apiClient.get<Record<string, string>>('/api/symbols/metadata')
  return data
}

export const updateSymbols = async (password: string, symbols: SymbolsResponse): Promise<void> => {
  await apiClient.post('/api/settings/symbols', {
    password,
    ...symbols,
  })
}

export const fetchSchedulerStatus = async (): Promise<{ enabled: boolean }> => {
  const { data } = await apiClient.get<{ enabled: boolean }>('/api/settings/scheduler')
  return data
}

export const updateSchedulerStatus = async (password: string, enabled: boolean): Promise<{ enabled: boolean }> => {
  const { data } = await apiClient.post<{ enabled: boolean }>('/api/settings/scheduler', {
    password,
    enabled,
  })
  return data
}

// ── OHLCV ──────────────────────────────────────────────────────────────────

export const fetchOHLCV = async (
  symbol: string,
  interval: Interval = '1d',
  limit: number = 100,
): Promise<{ symbol: string; interval: string; data: OHLCVCandle[] }> => {
  const { data } = await apiClient.get(`/api/ohlcv/${encodeURIComponent(symbol)}`, {
    params: { interval, limit },
  })
  return data
}

// ── Predictions ────────────────────────────────────────────────────────────

// Candles needed per interval to cover exactly 24h ahead (must match backend)
const INTERVAL_HORIZON: Record<string, number> = { '1h': 24, '4h': 6, '1d': 1 }

export const fetchPrediction = async (
  symbol: string,
  interval?: Interval,
  samples: number = 100,
): Promise<SymbolPrediction> => {
  const horizon = interval ? (INTERVAL_HORIZON[interval] ?? 24) : 24
  const { data } = await apiClient.get<SymbolPrediction>(
    `/api/predict/${encodeURIComponent(symbol)}`,
    { params: { interval, horizon, samples } },
  )
  return data
}

export const fetchPredictionEnsemble = async (
  symbol: string,
): Promise<import('../types').SymbolEnsemblePrediction> => {
  const { data } = await apiClient.get<import('../types').SymbolEnsemblePrediction>(
    `/api/predict/ensemble/${encodeURIComponent(symbol)}`
  )
  return data
}

// ── Alerts ─────────────────────────────────────────────────────────────────

export const fetchAlerts = async (
  limit: number = 50,
): Promise<{ alerts: Alert[] }> => {
  const { data } = await apiClient.get<{ alerts: Alert[] }>('/api/alerts', {
    params: { limit },
  })
  return data
}


// ── Status ─────────────────────────────────────────────────────────────────

export const fetchStatus = async (): Promise<{
  kronos_ready: boolean
  device: string
  symbols_count: number
}> => {
  const { data } = await apiClient.get('/api/status')
  return data
}

// ── Regime ────────────────────────────────────────────────────────────────

export const fetchRegime = async (symbol: string): Promise<RegimeResult> => {
  const { data } = await apiClient.get<RegimeResult>(
    `/api/regime/${encodeURIComponent(symbol)}`,
  )
  return data
}

// ── Indicators ────────────────────────────────────────────────────────────

export const fetchIndicators = async (
  symbol: string,
  interval: Interval = '1h',
  limit: number = 200,
): Promise<IndicatorsResult> => {
  const { data } = await apiClient.get<IndicatorsResult>(
    `/api/indicators/${encodeURIComponent(symbol)}`,
    { params: { interval, limit } },
  )
  return data
}

// ── Economic Calendar ─────────────────────────────────────────────────────

export const fetchEconomicCalendar = async (
  hours: number = 24,
): Promise<CalendarResponse> => {
  const { data } = await apiClient.get<CalendarResponse>('/api/economic-calendar', {
    params: { hours },
  })
  return data
}

// ── Currency Strength ─────────────────────────────────────────────────────

export const fetchCurrencyStrength = async (
  period: number = 14,
): Promise<CurrencyStrengthResult> => {
  const { data } = await apiClient.get<CurrencyStrengthResult>('/api/currency-strength', {
    params: { period },
  })
  return data
}

// ── Sentiment ─────────────────────────────────────────────────────────────

export const fetchSentiment = async (symbol: string): Promise<SentimentResult> => {
  const { data } = await apiClient.get<SentimentResult>(
    `/api/sentiment/${encodeURIComponent(symbol)}`,
  )
  return data
}

// ── Strategist ────────────────────────────────────────────────────────────

export const fetchStrategist = async (symbol: string): Promise<StrategistResult> => {
  const { data } = await apiClient.get<StrategistResult>(
    `/api/strategist/${encodeURIComponent(symbol)}`,
  )
  return data
}

// ── Active Trade Signal(実際の売買シグナル、AI軍師の自己申告とは別物) ──────────

export const fetchActiveSignal = async (symbol: string): Promise<TradeRecommendation> => {
  const { data } = await apiClient.get<TradeRecommendation>(
    `/api/trades/active-signal/${encodeURIComponent(symbol)}`,
  )
  return data
}


// ── Accuracy Stats ────────────────────────────────────────────────────────

export const fetchAccuracyStats = async (options: {
  symbol?:   string
  interval?: string
  limit?:    number
} = {}): Promise<AccuracyStats> => {
  const { data } = await apiClient.get<AccuracyStats>('/api/accuracy/stats', {
    params: {
      ...(options.symbol   && { symbol: options.symbol }),
      ...(options.interval && { interval: options.interval }),
      ...(options.limit    && { limit: options.limit }),
    },
  })
  return data
}

// ── Trade Log Stats ──────────────────────────────────────────────────────

export const fetchTradeStats = async (options: {
  symbol?:   string
  interval?: string
  limit?:    number
} = {}): Promise<TradeLogStats> => {
  const { data } = await apiClient.get<TradeLogStats>('/api/trades/stats', {
    params: {
      ...(options.symbol   && { symbol: options.symbol }),
      ...(options.interval && { interval: options.interval }),
      ...(options.limit    && { limit: options.limit }),
    },
  })
  return data
}

export default apiClient
